import { useEffect } from "react";
import { useDispatch, useStore } from "react-redux";

import { fetchUserDataAction } from "../slices/authSlice";

// Throttle: at most one revalidation per window, so rapid tab-switching can't
// storm the backend with re-fetches.
const MIN_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Recover a live tab from a stale workspace selection.
 *
 * Workspace membership is server-authoritative, but a long-open tab only
 * reconciles its persisted `selectedWorkspace` / `id_client` at login /
 * bootstrap (`fetchUserDataAction.fulfilled`) — not on Firebase's silent hourly
 * token renewal. So after a server-side membership change a never-reloaded tab
 * keeps sending a dead `id_client` and 403s on every scoped call.
 *
 * This hook re-validates the selection whenever the tab is resumed (becomes
 * visible, or the window regains focus), by re-dispatching `fetchUserDataAction`
 * in the background — its `.fulfilled` reducer re-checks the persisted selection
 * against fresh membership and heals it, proactively, before the user hits a
 * 403. Gated to authenticated, non-impersonating sessions with a profile to
 * reconcile against; a background fetch never drives the app spinner (see the
 * pending reducer). `recoverFromWorkspaceScoped403` is the reactive backstop for
 * a tab that stays focused through the membership change.
 *
 * State is read via `useStore().getState()` inside the listener (not
 * `useSelector`) so the long-lived handler always sees fresh values rather than
 * a stale closure — the same reason `storageOwner.js` reads the owner marker
 * inside its callback rather than closing over it.
 */
export const useWorkspaceRevalidation = () => {
  const dispatch = useDispatch();
  const store = useStore();

  useEffect(() => {
    let lastRun = 0;

    const revalidate = () => {
      if (document.visibilityState !== "visible") return;

      const { isAuthenticated, userData, isImpersonating, loading } =
        store.getState().auth;
      // Only meaningful once we have a fetched profile to reconcile against.
      if (!isAuthenticated || !userData || isImpersonating || loading) return;

      const now = Date.now();
      if (now - lastRun < MIN_INTERVAL_MS) return;
      lastRun = now;

      dispatch(fetchUserDataAction({ withScope: true, background: true }));
    };

    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [dispatch, store]);
};
